<template>
  <div class="asset-csv-import-wizard">
    <WizardProgress
      :current-step="currentStep"
      :steps="stepsToShow"
      :issue-counts="stepIssueCounts"
      :resolved-steps="resolvedSteps"
      @start-tour="startTour"
    />
    
    <div class="wizard-content">
      <StepUpload
        v-if="currentStep === 'upload'"
        :template-error="templateError"
        @file-selected="onFileSelected"
        @file-removed="onFileRemoved"
        @download-template="downloadTemplate"
      />
      
      <StepSummary
        v-if="currentStep === 'summary'"
        :validation-results="validationResults"
        :total-rows="validationResults.totalRows"
        :template-warning="templateWarning"
        @continue="goNext"
        @skip-to-review="goToReview"
      />

      <StepMissingFields
        v-if="currentStep === 'missing-fields'"
        :missing-fields="validationResults.missingFields"
      />
      
      <StepDuplicates
        v-if="currentStep === 'duplicates'"
        :duplicates="validationResults.duplicates"
        @update:duplicates="onDuplicatesUpdate"
        @all-resolved="goNext"
      />
      
      <StepWhitespace
        v-if="currentStep === 'whitespace'"
        :issues="validationResults.whitespaceIssues"
        @apply-fixes="onApplyWhitespaceFixes"
      />
      
      <StepValueHarmonisation
        v-if="currentStep === 'harmonisation'"
        :issues="validationResults.harmonisationIssues"
        @update:issues="onHarmonisationUpdate"
        @issue-resolved="onHarmonisationResolved"
        @all-resolved="goNext"
      />
      
      <StepReview
        v-if="currentStep === 'review'"
        :summary="importSummary"
        :changes-summary="changesSummary"
        :is-importing="isImporting"
        :active-import-action="activeImportAction"
        :import-progress="importProgress"
        :import-status="importStatus"
        :import-complete="importComplete"
        :import-error="importError"
        :can-import="canImport"
        :last-run-was-dry-run="lastRunWasDryRun"
        :report-available="reportAvailable"
        :can-commit-dry-run="canCommitDryRun"
        :dry-run-summary="dryRunReportSummary"
        :dry-run-details="dryRunReportDetails"
        :dry-run-report-error="dryRunReportError"
        @download-fixed-file="downloadFixedFile"
        @start-import="startImport"
        @commit-dry-run="commitDryRun"
        @download-dry-run-report="downloadDryRunReport"
        @done="resetWizard"
        @view-products="viewProducts"
      />
    </div>
    
    <div class="wizard-footer" v-if="currentStep !== 'upload' && !importComplete && !importError">
      <v-container fluid class="px-4">
        <v-row justify="center">
          <v-col cols="12" lg="11">
            <div class="d-flex align-center py-4">
              <v-btn 
                text 
                @click="goBack"
                :disabled="isImporting"
              >
                <v-icon left>mdi-chevron-left</v-icon>
                Back
              </v-btn>
              
              <v-spacer></v-spacer>
              
              <v-btn
                v-if="currentStep !== 'review' && currentStep !== 'summary'"
                color="primary"
                :disabled="!canProceed"
                @click="goNext"
              >
                Continue
                <v-icon right>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>
    
    <v-tour name="importWizardTour" :steps="tourSteps"></v-tour>
    
    <v-dialog v-model="showLeaveDialog" max-width="450" persistent>
      <v-card>
        <v-card-title class="text-h6 px-4">
          <v-icon left color="warning">mdi-alert</v-icon>
          Discard changes?
        </v-card-title>
        <v-card-text>
          You have an import in progress. If you leave now, your changes will be lost and you'll need to start again.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showLeaveDialog = false">
            Stay Here
          </v-btn>
          <v-btn color="error" text @click="confirmLeave">
            Discard & Leave
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import * as XLSX from 'xlsx';
import WizardProgress from './components/WizardProgress.vue';
import StepUpload from './steps/StepUpload.vue';
import StepSummary from './steps/StepSummary.vue';
import StepMissingFields from './steps/StepMissingFields.vue';
import StepDuplicates from './steps/StepDuplicates.vue';
import StepWhitespace from './steps/StepWhitespace.vue';
import StepValueHarmonisation from './steps/StepValueHarmonisation.vue';
import StepReview from './steps/StepReview.vue';

import { 
  parseExcelFile,
  validateTemplateFormat,
  validateImportData,
  applyDuplicateResolution,
  applyWhitespaceFixes,
  applyHarmonisationFixes,
  exportToExcel,
  generateImportSummary
} from './composables/useImportValidation';
import { downloadBlob, MIME_TYPES } from '../../../../services/fileDownload';

import { WIZARD_STEPS } from './composables/useWizardNavigation';

export default {
  name: 'AssetCsvImportWizard',
  components: {
    WizardProgress,
    StepUpload,
    StepSummary,
    StepMissingFields,
    StepDuplicates,
    StepWhitespace,
    StepValueHarmonisation,
    StepReview
  },
  data() {
    return {
      currentStep: WIZARD_STEPS.UPLOAD,
      stepsToShow: [WIZARD_STEPS.UPLOAD],
      selectedFile: null,
      parsedData: null,
      parsedHeaders: [],
      validationResults: {
        totalRows: 0,
        duplicates: [],
        missingFields: [],
        whitespaceIssues: [],
        harmonisationIssues: []
      },
      changesSummary: {
        duplicatesResolved: 0,
        rowsRemoved: 0,
        whitespaceCleaned: 0,
        valuesStandardized: 0
      },
      isImporting: false,
      activeImportAction: null,
      importProgress: 0,
      importStatus: '',
      importComplete: false,
      lastRunWasDryRun: false,
      reportAvailable: false,
      canCommitDryRun: false,
      dryRunReportSummary: {
        totalRows: 0,
        createCount: 0,
        updateCount: 0,
        skipCount: 0,
        errorCount: 0
      },
      dryRunReportDetails: [],
      dryRunReportError: null,
      lastDryRunJobId: null,
      importError: null,
      whitespacesApplied: false,
      preHarmonisationData: null,
      templateError: null,
      templateWarning: null,
      showLeaveDialog: false,
      pendingLeaveAction: null,
      pendingRouteNext: null,
      tourSteps: [
        {
          target: '#wizard-upload-btn',
          content: 'Start by selecting your Excel file (.xlsx). You can click this button or drag and drop your file directly onto the upload area.',
          params: {
            placement: 'bottom'
          }
        },
        {
          target: '#wizard-download-template',
          content: 'If you need the correct format, download our import template here. This ensures your data has all the required columns.',
          params: {
            placement: 'top'
          }
        },
        {
          target: '#wizard-field-info-btn',
          content: 'Click here to see which columns are required and what each field means.',
          params: {
            placement: 'top'
          }
        }
      ]
    };
  },
  computed: {
    ...mapGetters("authentication", ["GetAuthHeaderObj"]),
    
    importSummary() {
      if (!this.parsedData) {
        return {
          totalRows: 0,
          validRows: 0,
          newRecords: 0,
          updates: 0,
          sections: [],
          categories: []
        };
      }
      return generateImportSummary(this.parsedData);
    },
    
    canProceed() {
      switch (this.currentStep) {
        case WIZARD_STEPS.MISSING_FIELDS:
          return this.validationResults.missingFields.length === 0;
        case WIZARD_STEPS.DUPLICATES:
          return this.validationResults.duplicates
            .filter(d => d.severity === 'block')
            .every(d => d.resolved);
        case WIZARD_STEPS.WHITESPACE:
          return this.validationResults.whitespaceIssues.length === 0 || this.whitespacesApplied;
        case WIZARD_STEPS.HARMONISATION:
          return true;
        default:
          return true;
      }
    },
    
    canImport() {
      const hasUnresolvedBlocks = this.validationResults.duplicates
        .some(d => d.severity === 'block' && !d.resolved);
      const hasMissingFields = this.validationResults.missingFields.length > 0;
      return !hasUnresolvedBlocks && !hasMissingFields && !this.isImporting;
    },
    
    hasImportInProgress() {
      return this.selectedFile !== null && this.currentStep !== WIZARD_STEPS.UPLOAD && !this.importComplete;
    },
    
    stepIssueCounts() {
      return {
        upload: 0,
        summary: 0,
        'missing-fields': this.validationResults.missingFields.length,
        duplicates: this.validationResults.duplicates.filter(d => d.severity === 'block' && !d.resolved).length,
        whitespace: this.whitespacesApplied ? 0 : this.validationResults.whitespaceIssues.length,
        harmonisation: this.validationResults.harmonisationIssues.filter(issue => !issue.resolved).length,
        review: 0
      };
    },
    
    resolvedSteps() {
      return {
        upload: true,
        summary: true,
        'missing-fields': this.validationResults.missingFields.length === 0,
        duplicates: this.validationResults.duplicates.filter(d => d.severity === 'block').length === 0 || 
                    this.validationResults.duplicates.filter(d => d.severity === 'block').every(d => d.resolved),
        whitespace: this.validationResults.whitespaceIssues.length === 0 || 
                    this.whitespacesApplied,
        harmonisation: this.validationResults.harmonisationIssues.length === 0 || 
                       this.validationResults.harmonisationIssues.every(issue => issue.resolved),
        review: false
      };
    }
  },
  methods: {
    ...mapActions("assetxpstate", [
      "GetAssetImportTemplate",
      "ImportAssetsFromFile",
      "GetImportJobStatus",
      "CommitDryRunImport",
      "DownloadDryRunReport"
    ]),
    
    goBack() {
      const currentIndex = this.stepsToShow.indexOf(this.currentStep);
      if (currentIndex > 0) {
        const previousStep = this.stepsToShow[currentIndex - 1];
        if (previousStep === WIZARD_STEPS.UPLOAD && this.hasImportInProgress) {
          this.pendingLeaveAction = 'back-to-upload';
          this.showLeaveDialog = true;
        } else {
          this.currentStep = previousStep;
        }
      }
    },
    
    confirmLeave() {
      this.showLeaveDialog = false;
      if (this.pendingLeaveAction === 'back-to-upload') {
        this.resetWizard();
      } else if (this.pendingLeaveAction === 'browser-back') {
        this.resetWizard();
        this.$emit('wizard-cancelled');
      } else if (this.pendingLeaveAction === 'route-leave') {
        if (this.pendingRouteNext) {
          this.pendingRouteNext();
          this.pendingRouteNext = null;
        }
      }
      this.pendingLeaveAction = null;
    },
    
    resetWizard() {
      this.currentStep = WIZARD_STEPS.UPLOAD;
      this.stepsToShow = [WIZARD_STEPS.UPLOAD];
      this.selectedFile = null;
      this.parsedData = null;
      this.parsedHeaders = [];
      this.validationResults = {
        totalRows: 0,
        duplicates: [],
        missingFields: [],
        whitespaceIssues: [],
        harmonisationIssues: []
      };
      this.changesSummary = {
        duplicatesResolved: 0,
        rowsRemoved: 0,
        whitespaceCleaned: 0,
        valuesStandardized: 0
      };
      this.whitespacesApplied = false;
      this.preHarmonisationData = null;
      this.importComplete = false;
      this.activeImportAction = null;
      this.lastRunWasDryRun = false;
      this.reportAvailable = false;
      this.canCommitDryRun = false;
      this.dryRunReportSummary = {
        totalRows: 0,
        createCount: 0,
        updateCount: 0,
        skipCount: 0,
        errorCount: 0
      };
      this.dryRunReportDetails = [];
      this.dryRunReportError = null;
      this.lastDryRunJobId = null;
      this.importError = null;
    },
    
    handleBeforeUnload(event) {
      if (this.hasImportInProgress && !this.importComplete) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    },
    
    handlePopState(event) {
      if (event.state && event.state.wizardStep) {
        const targetStep = event.state.wizardStep;
        
        if (targetStep === WIZARD_STEPS.UPLOAD && this.hasImportInProgress) {
          this.pendingLeaveAction = 'browser-back';
          this.showLeaveDialog = true;
          history.pushState({ wizardStep: this.currentStep }, '', '');
        } else if (this.stepsToShow.includes(targetStep)) {
          this.currentStep = targetStep;
        }
      } else if (this.hasImportInProgress) {
        if (this.currentStep !== WIZARD_STEPS.UPLOAD) {
          const currentIndex = this.stepsToShow.indexOf(this.currentStep);
          if (currentIndex > 0) {
            this.currentStep = this.stepsToShow[currentIndex - 1];
            history.replaceState({ wizardStep: this.currentStep }, '', '');
          }
        } else {
          this.pendingLeaveAction = 'browser-back';
          this.showLeaveDialog = true;
          history.pushState({ wizardStep: this.currentStep }, '', '');
        }
      }
    },
    
    goNext() {
      const currentIndex = this.stepsToShow.indexOf(this.currentStep);
      if (currentIndex < this.stepsToShow.length - 1) {
        const nextStep = this.stepsToShow[currentIndex + 1];
        history.pushState({ wizardStep: nextStep }, '', '');
        this.currentStep = nextStep;
      }
    },
    
    goToStep(step) {
      if (this.stepsToShow.includes(step)) {
        history.pushState({ wizardStep: step }, '', '');
        this.currentStep = step;
      }
    },
    
    goToReview() {
      if (this.validationResults.missingFields.length > 0) {
        return;
      }

      history.pushState({ wizardStep: WIZARD_STEPS.REVIEW }, '', '');
      this.currentStep = WIZARD_STEPS.REVIEW;
    },
    
    async onFileSelected(file) {
      this.selectedFile = file;
      this.templateError = null;
      this.templateWarning = null;
      
      const result = await parseExcelFile(file);
      
      if (result.error) {
        console.error(result.error);
        return;
      }
      
      this.parsedData = result.rows;
      this.parsedHeaders = result.headers;
      
      const templateValidation = validateTemplateFormat(result.headers);
      if (!templateValidation.isValid) {
        this.templateError = templateValidation;
        return;
      }
      
      if (templateValidation.missingOptional.length > 0) {
        this.templateWarning = templateValidation;
      }
      
      this.validationResults = validateImportData(result.rows, result.headers);
      this.buildStepsList();
      this.goNext();
    },
    
    onFileRemoved() {
      this.selectedFile = null;
      this.parsedData = null;
      this.parsedHeaders = [];
      this.templateError = null;
      this.templateWarning = null;
      this.validationResults = {
        totalRows: 0,
        duplicates: [],
        missingFields: [],
        whitespaceIssues: [],
        harmonisationIssues: []
      };
      this.stepsToShow = [WIZARD_STEPS.UPLOAD];
      this.currentStep = WIZARD_STEPS.UPLOAD;
      this.resetChangesSummary();
    },
    
    buildStepsList() {
      const steps = [
        WIZARD_STEPS.UPLOAD,
        WIZARD_STEPS.SUMMARY,
        WIZARD_STEPS.MISSING_FIELDS,
        WIZARD_STEPS.DUPLICATES,
        WIZARD_STEPS.WHITESPACE,
        WIZARD_STEPS.HARMONISATION,
        WIZARD_STEPS.REVIEW
      ];
      this.stepsToShow = steps;
    },
    
    onDuplicatesUpdate(duplicates) {
      this.validationResults.duplicates = duplicates;
    },
    
    applyDuplicateResolutions() {
      const resolvedCount = this.validationResults.duplicates.filter(d =>
        d.resolved && d.resolution !== 'keep-all' && d.resolution !== 'skip'
      ).length;
      
      if (resolvedCount > 0) {
        const originalLength = this.parsedData.length;
        this.parsedData = applyDuplicateResolution(this.parsedData, this.validationResults.duplicates);
        
        this.changesSummary.duplicatesResolved = resolvedCount;
        this.changesSummary.rowsRemoved = originalLength - this.parsedData.length;
        
        this.validationResults = validateImportData(this.parsedData, this.parsedHeaders);
        this.buildStepsList();
      }
    },
    
    onApplyWhitespaceFixes(issues) {
      this.parsedData = applyWhitespaceFixes(this.parsedData, issues);
      this.changesSummary.whitespaceCleaned = issues.length;
      this.whitespacesApplied = true;
      this.validationResults = validateImportData(this.parsedData, this.parsedHeaders);
    },
    
    onHarmonisationUpdate(issues) {
      this.validationResults.harmonisationIssues = issues;
    },
    
    onHarmonisationResolved() {
      this.recalcHarmonisationCount();
    },
    
    recalcHarmonisationCount() {
      let total = 0;
      this.validationResults.harmonisationIssues.forEach(issue => {
        if (!issue.resolved || !issue.selectedValue) return;
        const targetValue = issue.selectedValue === '__custom__'
          ? issue.customValue
          : issue.selectedValue;
        if (!targetValue) return;
        const ignored = issue.ignoredVariants || [];
        issue.variants.forEach(v => {
          if (!ignored.includes(v.value) && v.value !== targetValue) {
            total += v.count;
          }
        });
      });
      this.changesSummary.valuesStandardized = total;
    },
    
    applyHarmonisationFixes() {
      if (!this.preHarmonisationData) {
        this.preHarmonisationData = JSON.parse(JSON.stringify(this.parsedData));
      }
      this.validationResults.harmonisationIssues.forEach(issue => {
        if (issue.resolved) return;
        const hasValidSelection = issue.selectedValue === '__custom__'
          ? issue.customValue && issue.customValue.trim().length > 0
          : !!issue.selectedValue;
        if (hasValidSelection) {
          issue.resolved = true;
        }
      });
      this.recalcHarmonisationCount();
      this.parsedData = applyHarmonisationFixes(
        this.preHarmonisationData, 
        this.validationResults.harmonisationIssues
      );
    },
    
    async downloadTemplate() {
      await this.GetAssetImportTemplate();
    },
    
    downloadFixedFile() {
      this.applyAllPendingFixes();
      
      const fileName = this.selectedFile 
        ? this.selectedFile.name.replace('.xlsx', '_fixed.xlsx')
        : 'import_fixed.xlsx';
      
      const blob = exportToExcel(this.parsedData);
      downloadBlob(blob, fileName);
    },
    
    applyAllPendingFixes() {
      if (this.validationResults.duplicates.some(d => d.resolved && d.resolution !== 'keep-all' && d.resolution !== 'skip')) {
        this.applyDuplicateResolutions();
      }
      
      if (this.validationResults.harmonisationIssues.some(h => h.resolved && h.selectedValue)) {
        this.applyHarmonisationFixes();
      }
    },
    
    async startImport() {
      this.applyAllPendingFixes();
      this.importError = null;
      this.dryRunReportError = null;
      
      const formData = new FormData();
      const blob = exportToExcel(this.parsedData);
      const fileName = this.selectedFile ? this.selectedFile.name : 'import.xlsx';
      const file = new File([blob], fileName, {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      formData.append('file', file, fileName);
      
      this.isImporting = true;
      this.activeImportAction = 'dryRun';
      this.importProgress = 0;
      this.importStatus = 'Uploading file and running dry run...';
      this.lastRunWasDryRun = true;
      this.reportAvailable = false;
      this.canCommitDryRun = false;
      this.dryRunReportSummary = {
        totalRows: 0,
        createCount: 0,
        updateCount: 0,
        skipCount: 0,
        errorCount: 0
      };
      this.dryRunReportDetails = [];
      
      try {
        const response = await this.ImportAssetsFromFile({ formData, dryRun: true });
        this.jobStatusId = response.data;
        this.watchJobStatus();
      } catch (error) {
        console.error('Import failed:', error);
        this.importError = error.response?.data?.message || error.message || 'Failed to upload file. Please try again.';
        this.isImporting = false;
        this.activeImportAction = null;
      }
    },

    async commitDryRun() {
      if (!this.lastDryRunJobId) {
        return;
      }

      this.importError = null;
      this.importComplete = false;
      this.isImporting = true;
      this.activeImportAction = 'commitDryRun';
      this.importProgress = 0;
      this.importStatus = 'Starting commit from dry run...';
      this.lastRunWasDryRun = false;
      this.canCommitDryRun = false;

      try {
        const response = await this.CommitDryRunImport(this.lastDryRunJobId);
        this.jobStatusId = response.data;
        this.watchJobStatus();
      } catch (error) {
        this.importError = error.response?.data?.message || error.message || 'Failed to commit dry run.';
        this.isImporting = false;
        this.activeImportAction = null;
      }
    },

    async downloadDryRunReport() {
      if (!this.lastDryRunJobId) {
        return;
      }

      try {
        const response = await this.DownloadDryRunReport(this.lastDryRunJobId);
        const blob = new Blob([response.data], { type: MIME_TYPES.xlsx });
        downloadBlob(blob, `${this.lastDryRunJobId}-dryrun-report.xlsx`);
      } catch (error) {
        this.importError = error.response?.data?.message || error.message || 'Failed to download dry run report.';
      }
    },
    
    async watchJobStatus() {
      const checkStatus = async () => {
        try {
          const response = await this.GetImportJobStatus(this.jobStatusId);
          const status = response.data;
          
          this.importProgress = status.percentComplete * 100;
          this.importStatus = status.statusMessage;
          
          if (status.completitionState === 0) {
            setTimeout(checkStatus, 1500);
          } else if (status.completitionState === 1) {
            this.importComplete = true;
            this.isImporting = false;
            this.activeImportAction = null;
            this.lastRunWasDryRun = status.dryRun === true;
            this.reportAvailable = status.reportAvailable === true;
            this.canCommitDryRun = status.canCommit === true;
            this.lastDryRunJobId = status.dryRun === true ? this.jobStatusId : null;

            if (status.dryRun === true && this.lastDryRunJobId) {
              await this.loadDryRunReportData(this.lastDryRunJobId);
            }

            this.$emit('import-complete', { success: true });
          } else {
            this.importError = status.statusMessage || 'Import failed. Please try again.';
            this.isImporting = false;
            this.activeImportAction = null;
            this.$emit('import-complete', { success: false, error: status.statusMessage });
          }
        } catch (error) {
          console.error('Failed to check status:', error);
          this.importError = 'Failed to check import status. Please try again.';
          this.isImporting = false;
          this.activeImportAction = null;
        }
      };
      
      checkStatus();
    },

    async loadDryRunReportData(jobId) {
      try {
        const response = await this.DownloadDryRunReport(jobId);
        const buffer = await response.data.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });

        this.dryRunReportSummary = this.parseDryRunSummarySheet(workbook);
        this.dryRunReportDetails = this.parseDryRunDetailsSheet(workbook);
        this.dryRunReportError = null;
      } catch (error) {
        this.dryRunReportError = error?.message || 'Could not load dry run report data.';
        this.dryRunReportSummary = {
          totalRows: 0,
          createCount: 0,
          updateCount: 0,
          skipCount: 0,
          errorCount: 0
        };
        this.dryRunReportDetails = [];
      }
    },

    parseDryRunSummarySheet(workbook) {
      const worksheet = workbook.Sheets['Summary'];
      if (!worksheet) {
        return {
          totalRows: 0,
          createCount: 0,
          updateCount: 0,
          skipCount: 0,
          errorCount: 0
        };
      }

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        defval: ''
      });

      const metricLookup = new Map();
      rows.forEach((row) => {
        if (!row || row.length < 2) {
          return;
        }

        const label = String(row[0] || '').trim();
        const value = Number(row[1]) || 0;
        if (label) {
          metricLookup.set(label, value);
        }
      });

      return {
        totalRows: metricLookup.get('Total Rows:') || 0,
        createCount: metricLookup.get('Assets to Create:') || 0,
        updateCount: metricLookup.get('Assets to Update:') || 0,
        skipCount: metricLookup.get('Assets Skipped:') || 0,
        errorCount: metricLookup.get('Assets with Errors:') || 0
      };
    },

    parseDryRunDetailsSheet(workbook) {
      const worksheet = workbook.Sheets['Details'];
      if (!worksheet) {
        return [];
      }

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        defval: ''
      });

      return rows.map((row) => ({
        rowNumber: row['Row #'],
        identifier: row.Identifier,
        action: row.Action,
        field: row.Field,
        oldValue: row['Old Value'],
        newValue: row['New Value'],
        notes: row.Notes
      }));
    },
    
    viewProducts() {
      const sections = this.importSummary.sections;
      if (sections.length > 0) {
        this.$router.push(`/section/${encodeURIComponent(sections[0])}/search`);
      }
    },
    
    resetChangesSummary() {
      this.changesSummary = {
        duplicatesResolved: 0,
        rowsRemoved: 0,
        whitespaceCleaned: 0,
        valuesStandardized: 0
      };
    },
    
    startTour() {
      this.updateTourSteps();
      this.$tours['importWizardTour'].start();
    },
    
    updateTourSteps() {
      if (this.currentStep === WIZARD_STEPS.UPLOAD) {
        this.tourSteps = [
          {
            target: '#wizard-upload-btn',
            content: 'Start by selecting your Excel file (.xlsx). You can click this button or drag and drop your file directly onto the upload area.',
            params: { placement: 'bottom' }
          },
          {
            target: '#wizard-download-template',
            content: 'If you need the correct format, download our import template here. This ensures your data has all the required columns.',
            params: { placement: 'top' }
          },
          {
            target: '#wizard-field-info-btn',
            content: 'Click here to see which columns are required and what each field means.',
            params: { placement: 'top' }
          }
        ];
      } else {
        this.tourSteps = [
          {
            target: '.wizard-progress',
            content: 'This shows your progress through the import process. The progress bar fills as you complete each step.',
            params: { placement: 'bottom' }
          },
          {
            target: '.wizard-content',
            content: 'Follow the instructions on each step to clean up any issues with your data. Resolving these before import ensures a smooth process.',
            params: { placement: 'bottom' }
          },
          {
            target: '.wizard-footer',
            content: 'Use these buttons to navigate between steps. Click "Continue" when you\'re ready to move on, or "Back" to return to a previous step.',
            params: { placement: 'bottom' }
          }
        ];
      }
    }
  },
  
  watch: {
    currentStep(newStep, oldStep) {
      if (oldStep === WIZARD_STEPS.DUPLICATES) {
        this.applyDuplicateResolutions();
      }
      if (oldStep === WIZARD_STEPS.HARMONISATION) {
        this.applyHarmonisationFixes();
      }
    },
    hasImportInProgress: {
      immediate: true,
      handler(val) {
        this.$emit('has-changes', val);
      }
    }
  },
  
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    window.addEventListener('popstate', this.handlePopState);
    history.replaceState({ wizardStep: this.currentStep }, '', '');
  },
  
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    window.removeEventListener('popstate', this.handlePopState);
  },
  
  beforeRouteLeave(to, from, next) {
    if (this.hasImportInProgress && !this.importComplete) {
      this.pendingLeaveAction = 'route-leave';
      this.pendingRouteNext = next;
      this.showLeaveDialog = true;
    } else {
      next();
    }
  }
};
</script>

<style scoped>
.asset-csv-import-wizard {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  background: #fff;
}

.wizard-content {
  flex: 1;
  overflow-y: auto;
}

.wizard-footer {
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
}
</style>
